import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// import React, { useEffect } from "react"; // Store Custom Code
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// Store Custom Code
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Hidden from "@insite/mobius/Hidden";
import { COLORS, TYPOGRAPHY } from "@store/theme/design";
import { siteWidthStyles } from "@store/theme/mixins";
import React, { useEffect, useState } from "react";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    headerSection?: InjectableCss;
    headerBannerSection?: InjectableCss;
    headerBannerSectionMobileWrapper?: InjectableCss;
    headerBannerSectionItem?: InjectableCss;
    websiteName?: InjectableCss;
    toggleBottomButton?: ButtonPresentationProps;
    headerPage: InjectableCss; // Store Custom Code
}

// Store Custom Code
export const headerStyles: HeaderStyles = {
    headerSection: {
        css: css`
            ${siteWidthStyles(["0", "0", "0", "45px", "60px"])};
            background-color: white;
            font-size: ${TYPOGRAPHY.size.smaller};
        `,
    },
    headerBannerSectionMobileWrapper: {
        css: css`
            padding: 8px;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    headerBannerSection: {
        css: css`
            min-height: 5px;
            position: relative;
            background-color: ${COLORS.primary.base};
            > div {
                position: relative;
                z-index: 2;
            }

            &:before {
                content: "";
                position: absolute;
                top: 0;
                bottom: 0;
                left: -9998px;
                right: 0;
                border-left: 9999px solid ${COLORS.primary.base};
                box-shadow: 9999px 0 0 ${COLORS.primary.base};
            }
        `,
    },
    headerBannerSectionItem: {
        css: css`
            ${siteWidthStyles()}
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: ${TYPOGRAPHY.size.tiny};
            padding-left: 15px;
            display: none;
        `,
    },
    toggleBottomButton: {
        css: css`
            width: 100% !important;
        `,
    },
    headerPage: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            overflow: visible;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = headerStyles;

const Header = ({ id, siteName, session, toaster, history, myAccountPageUrl, dismissRfqUpdates }: Props) => {
    const [isBottomMobileOpen, setIsBottomMobileOpen] = useState(false); // Store Custom Code
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    // Store Custom Code
    const toggleExpanded = () => {
        global.scrollTo(0, 0);
        setIsBottomMobileOpen(isBottomMobileOpen => !isBottomMobileOpen);
    };

    return (
        // Store Custom Code - Start
        <Page as="header" isHeader={true} {...styles.headerPage} padding={0}>
            <SkipNav
                extendedStyles={styles.skipToContent}
                text={translate("Skip to main content")}
                destination={afterHeader}
            />
            <EagerLoadingLogoProvider value={true}>
                <StyledWrapper {...styles.headerSection}>
                    <Zone contentId={id} zoneName="HeaderMainSection" fixed />
                </StyledWrapper>
                <Hidden below="md">
                    <StyledWrapper {...styles.headerBannerSection}>
                        <StyledWrapper {...styles.headerBannerSectionItem}>
                            <Zone contentId={id} zoneName="headerBannerSection" />
                        </StyledWrapper>
                    </StyledWrapper>
                </Hidden>
                <Hidden above="sm">
                    <StyledWrapper {...styles.headerBannerSectionMobileWrapper}>
                        {!isBottomMobileOpen && (
                            <Button {...styles.toggleBottomButton} onClick={toggleExpanded}>
                                {translate("Find bus parts")}
                            </Button>
                        )}
                        {isBottomMobileOpen && (
                            <StyledWrapper {...styles.headerBannerSection}>
                                <StyledWrapper {...styles.headerBannerSectionItem}>
                                    <Zone contentId={id} zoneName="headerBannerSection" />
                                    <Button {...styles.toggleBottomButton} onClick={toggleExpanded}>
                                        {translate("Hide")}
                                    </Button>
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </Hidden>
                <Zone contentId={id} zoneName="Breadcrumb" />
            </EagerLoadingLogoProvider>
            <span ref={afterHeader} tabIndex={-1} />
        </Page>
        // Store Custom Code - End
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
